import React, { useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { OtpInput } from "newui/components/ui/OtpInput";
import { Button } from "newui/components/Button";
import { useVerifyCodeMutation } from "./hooks/useVerifyCodeMutation";
import type { RegistrationPrefill } from "../types/userInvite";

interface VerificationCodeStepProps {
  inviteToken: string;
  maskedEmail: string;
  /**
   * ISO-8601 expiry for the issued code. When present we render the
   * exact "expires at HH:mm" datetime; when absent we fall back to the
   * generic "in 1 hour" copy.
   */
  verificationExpiresAt?: string | null;
  onSuccess: (registrationToken: string, prefill: RegistrationPrefill) => void;
}

const formatLockoutTime = (lockedUntil: string): string => {
  const d = new Date(lockedUntil);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatExpiry = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const VerificationCodeStep: React.FC<VerificationCodeStepProps> = ({
  inviteToken,
  maskedEmail,
  verificationExpiresAt,
  onSuccess,
}) => {
  const [code, setCode] = useState("");
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  const verifyCode = useVerifyCodeMutation();

  const isLocked = lockedUntil != null;

  // Clipboard sniff is now handled inside OtpInput on first cell focus —
  // gated on a real user gesture so the browser permission prompt actually
  // fires and we never overwrite a clipboard the user didn't intend for the
  // OTP. See OtpInput.handleFirstFocus.

const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 8 || isLocked || verifyCode.isPending) {
      return;
    }
    setCodeError(null);

    verifyCode.mutate(
      { token: inviteToken, code },
      {
        onSuccess: (data) => {
          onSuccess(data.short_lived_registration_token, data.prefill);
        },
        onError: (err) => {
          setCode("");
          if (err?.code === "VERIFICATION_CODE_LOCKED") {
            const until = err?.errors?.locked_until ?? null;
            setLockedUntil(until as string | null);
            setCodeError(
              `Too many attempts. Try again at ${until ? formatLockoutTime(until as string) : "a later time"}.`
            );
          } else {
            const remaining = err?.errors?.attempts_remaining;
            if (remaining != null) {
              setAttemptsRemaining(remaining as number);
            }
            setCodeError(
              `Invalid code.${remaining != null ? ` ${remaining} attempt${(remaining as number) !== 1 ? "s" : ""} remaining.` : ""}`
            );
          }
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Enter verification code</h2>
        <p className="mt-1 text-sm text-gray-600">
          We sent a code to <strong>{maskedEmail}</strong>. It expires in 1 hour
          {verificationExpiresAt && (
            <>
              {" "}
              (<strong>{formatExpiry(verificationExpiresAt)}</strong>)
            </>
          )}
          .
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="verify-code-form">
        <OtpInput
          value={code}
          onChange={setCode}
          disabled={isLocked || verifyCode.isPending}
          error={codeError ?? undefined}
        />

        {attemptsRemaining != null && !isLocked && (
          <p className="text-xs text-amber-700" data-testid="attempts-remaining">
            {attemptsRemaining} attempt{attemptsRemaining !== 1 ? "s" : ""} remaining before lockout.
          </p>
        )}

        <Button
          type="submit"
          disabled={code.length < 8 || isLocked || verifyCode.isPending}
          className="w-full"
          data-testid="verify-code-submit"
        >
          {verifyCode.isPending ? <Spinner size="sm" /> : "Verify code"}
        </Button>
      </form>
    </div>
  );
};
